import { actorEmail, auditStamp, auditTarget } from "../../../api/mcp";
import { AuditChangeDetail } from "../../../components/audit/AuditChangeDetail";
import { ResourceView } from "../../../components/states";
import { Badge, Card } from "../../../components/ui";
import { Button, Text } from "../../../f7";
import type { AnyAuditEvent } from "../../../hooks/useAuditFeed";
import type { PageOf } from "../../../hooks/usePagedList";
import type { Resource } from "../../../hooks/useResource";
import { ProductTag } from "../../../products/PerProduct";

type TaggedChange = AnyAuditEvent & { product: "mcp" | "labeeb" };

/**
 * Both products' newest audit pages, dated rows first.
 *
 * A row with no stamp sorts last. Putting it first would call an undated
 * change the most recent thing that happened; `new Date(null)` is the epoch,
 * which this console has already rendered as 1/1/1970.
 */
function mergeAuditTails(mcp: AnyAuditEvent[], labeeb: AnyAuditEvent[]): TaggedChange[] {
  const rows: TaggedChange[] = [
    ...mcp.map((event) => ({ ...event, product: "mcp" as const })),
    ...labeeb.map((event) => ({ ...event, product: "labeeb" as const })),
  ];
  return rows.sort((a, b) => {
    if (a.timestamp && b.timestamp) {
      if (a.timestamp === b.timestamp) return 0;
      return a.timestamp < b.timestamp ? 1 : -1;
    }
    if (a.timestamp) return -1;
    if (b.timestamp) return 1;
    return 0;
  });
}

/** Both audit tails, merged. A failure is a notice, not an empty log. */
export function RecentChanges({
  mcp,
  labeeb,
}: {
  mcp: { state: Resource<PageOf<AnyAuditEvent>>; reload: () => void };
  labeeb: { state: Resource<PageOf<AnyAuditEvent>>; reload: () => void };
}) {
  const mcpRows = mcp.state.kind === "ready" ? mcp.state.data.rows : [];
  const labeebRows = labeeb.state.kind === "ready" ? labeeb.state.data.rows : [];
  const merged = mergeAuditTails(mcpRows, labeebRows);
  const bothReady = mcp.state.kind === "ready" && labeeb.state.kind === "ready";

  return (
    <Card
      title="Recent changes"
      note="Fleet-wide · newest first"
      aside={
        <Button plain href="/governance/audit">
          Full audit
        </Button>
      }
    >
      {mcp.state.kind !== "ready" && (
        <ResourceView state={mcp.state} what="the audit tail" onRetry={mcp.reload} />
      )}
      {labeeb.state.kind !== "ready" && (
        <ResourceView state={labeeb.state} what="Labeeb's audit tail" onRetry={labeeb.reload} />
      )}
      {merged.length > 0 && <AuditTail rows={merged.slice(0, 6)} />}
      {bothReady && merged.length === 0 && <EmptyAudit />}
    </Card>
  );
}

function AuditTail({ rows }: { rows: TaggedChange[] }) {
  return (
    <div className="divide-y divide-slate-950/5 dark:divide-white/10">
      {rows.map((e, i) => (
        <div key={`${e.product}-${e.timestamp ?? "none"}-${e.action}-${i}`} className="flex items-start justify-between gap-3 py-2.5">
          <div className="min-w-0">
            <p className="truncate text-sm/5 text-slate-950 dark:text-white">
              <ProductTag id={e.product} />{" "}
              <span className="ref">{e.action}</span> · {auditTarget(e.target) ?? "not recorded"}
            </p>
            {/* `auditStamp`, not `new Date(...)`: a row with no stamp gave
                `new Date(null)` — the epoch — and rendered "1/1/1970" as
                though that were when the change happened. */}
            <p className="truncate font-mono text-[10px] tabular-nums text-slate-400 dark:text-slate-500">
              {auditStamp(e.timestamp)} · {actorEmail(e.actor)}
            </p>
            <div className="mt-1">
              <AuditChangeDetail compact before={e.before} after={e.after} action={e.action} />
            </div>
          </div>
          <Badge tone="neutral">{e.domain}</Badge>
        </div>
      ))}
    </div>
  );
}

function EmptyAudit() {
  return (
    // The design's empty panel: a filled box that says what would land here
    // and offers the way to the log. A bare Notice said only "nothing yet".
    <div className="rounded-lg bg-slate-50 p-6 text-center ring-1 ring-slate-950/5 dark:bg-white/5 dark:ring-white/10">
      <Text>No admin writes recorded yet.</Text>
      <p className="mt-1 text-xs/5 text-slate-500 dark:text-slate-400">
        Every change made here lands in the audit log with its read-back.
      </p>
      <div className="mt-3">
        <Button outline href="/governance/audit">
          Open audit log
        </Button>
      </div>
    </div>
  );
}
